/**
 * Выбор диапазона дат в стиле YouTube Studio
 */

import { useState } from 'react';
import { CalendarDays, ChevronDown, ChevronLeft } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Calendar } from '../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';

import { getRecentMonths, resolveDateRange } from '../lib/date-range';

import type { DatePresetId, DateRangeOption } from '../types/account-stats';
import type { DateRange } from 'react-day-picker';

type DateRangeSelectorProps = {
  /** `null` — период не задан (только вместе с `emptyLabel`). */
  value: DateRangeOption | null;
  onChange: (option: DateRangeOption) => void;
  /** Подпись «без периода» и пункт сброса — для форм, где период необязателен. */
  emptyLabel?: string;
  onClear?: () => void;
  disabled?: boolean;
  className?: string;
  /**
   * Открывать сразу календарь, а готовые периоды — по кнопке назад. Для форм,
   * где обычно вводят свои даты (выгрузки), а не «последние 30 дней».
   */
  customFirst?: boolean;
};

const PRESETS: Array<{ id: DatePresetId; label: string }> = [
  { id: 'lastWeek', label: 'Прошлая неделя' },
  { id: 'last7', label: 'Последние 7 дней' },
  { id: 'last30', label: 'Последние 30 дней' },
  { id: 'last90', label: 'Последние 90 дней' },
  { id: 'last365', label: 'Последние 365 дней' },
  { id: 'all', label: 'За все время' },
];

function isPresetActive(value: DateRangeOption | null, id: DatePresetId): boolean {
  return value?.type === 'preset' && value.id === id;
}

function isMonthActive(value: DateRangeOption | null, year: number, month: number): boolean {
  return value?.type === 'month' && value.year === year && value.month === month;
}

export function DateRangeSelector({
  value,
  onChange,
  emptyLabel,
  onClear,
  disabled,
  className,
  customFirst,
}: DateRangeSelectorProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customRange, setCustomRange] = useState<DateRange | undefined>();

  const resolved = value ? resolveDateRange(value) : null;
  const recentMonths = getRecentMonths(3);

  function handlePreset(id: DatePresetId): void {
    onChange({ type: 'preset', id });
    setOpen(false);
    setShowCustom(false);
  }

  function handleMonth(year: number, month: number): void {
    onChange({ type: 'month', year, month });
    setOpen(false);
    setShowCustom(false);
  }

  function handleCustomApply(): void {
    if (customRange?.from && customRange?.to) {
      onChange({ type: 'custom', from: customRange.from, to: customRange.to });
      setOpen(false);
      setShowCustom(false);
      setCustomRange(undefined);
    }
  }

  function handleOpenChange(nextOpen: boolean): void {
    setOpen(nextOpen);
    if (nextOpen && customFirst) {
      setShowCustom(true);
      // Текущий период уже отмечен: поправить одну границу — два клика.
      setCustomRange(resolved && value?.type !== 'preset' ? { from: resolved.from, to: resolved.to } : undefined);
    }
    if (!nextOpen) {
      setShowCustom(false);
      setCustomRange(undefined);
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={`h-auto flex-row items-center px-3 py-1.5 min-w-[200px] text-left gap-3 ${className ?? ''}`}
        >
          <div className="flex flex-col flex-1 gap-0">
            <span className="text-xs text-muted-foreground font-normal leading-tight">
              {resolved ? resolved.dateLabel : 'Период'}
            </span>
            <span className="font-medium text-sm leading-tight">
              {resolved ? resolved.label : (emptyLabel ?? 'Не выбран')}
            </span>
          </div>
          <ChevronDown className="size-4 text-muted-foreground shrink-0" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-0 overflow-hidden">
        {showCustom ? (
          <div className="flex flex-col">
            <div className="flex items-center gap-1 px-2 py-2 border-b">
              {customFirst ? (
                // Здесь календарь — главный вид, поэтому назад ведёт вся строка с подписью.
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-1.5"
                  onClick={() => setShowCustom(false)}
                >
                  <ChevronLeft className="size-4" />
                  Готовые периоды
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => {
                      setShowCustom(false);
                      setCustomRange(undefined);
                    }}
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                  <span className="text-sm font-medium">Другой диапазон дат</span>
                </>
              )}
            </div>

            <Calendar
              mode="range"
              selected={customRange}
              onSelect={setCustomRange}
              numberOfMonths={1}
              disabled={{ after: new Date() }}
              className="rounded-none border-0"
            />

            <div className="px-3 pb-3">
              <Button
                size="sm"
                className="w-full"
                disabled={!customRange?.from || !customRange?.to}
                onClick={handleCustomApply}
              >
                Применить
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-1">
            {PRESETS.map(preset => (
              <button
                key={preset.id}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-muted/60 transition-colors ${
                  isPresetActive(value, preset.id)
                    ? 'font-semibold text-primary bg-primary/5'
                    : 'text-foreground'
                }`}
                onClick={() => handlePreset(preset.id)}
              >
                {preset.label}
              </button>
            ))}

            <div className="border-t my-1" />

            {recentMonths.map(({ year, month, label }) => (
              <button
                key={`${year}-${month}`}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-muted/60 transition-colors ${
                  isMonthActive(value, year, month)
                    ? 'font-semibold text-primary bg-primary/5'
                    : 'text-foreground'
                }`}
                onClick={() => handleMonth(year, month)}
              >
                {label}
              </button>
            ))}

            <div className="border-t my-1" />

            <button
              className={`w-full text-left px-4 py-2 text-sm hover:bg-muted/60 transition-colors flex items-center gap-2 ${
                value?.type === 'custom'
                  ? 'font-semibold text-primary bg-primary/5'
                  : 'text-foreground'
              }`}
              onClick={() => setShowCustom(true)}
            >
              <CalendarDays className="size-4 text-muted-foreground shrink-0" />
              Другой диапазон дат
            </button>

            {onClear && emptyLabel && (
              <>
                <div className="border-t my-1" />
                <button
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-muted/60 transition-colors ${
                    value === null ? 'font-semibold text-primary bg-primary/5' : 'text-foreground'
                  }`}
                  onClick={() => {
                    onClear();
                    setOpen(false);
                  }}
                >
                  {emptyLabel}
                </button>
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
